import { appConfig, type ProductEntityType } from 'shared';
import { reportCriticalError } from '~/lib/tracing';
import { getLocalUserDb } from '~/query/local-user-db';

/** Client-side `failed_sync` quarantine row for failed offline mutation replay. */
export interface FailedSyncRecord {
  /** Auto-increment primary key. */
  id?: number;
  /** Mutation id (stx.mutationId) for idempotent manual replay. */
  mutationId: string;
  /** Product entity type, when known. */
  entityType?: ProductEntityType;
  /** Client cache version (appConfig.clientCacheVersion) the client was on when it failed. */
  clientCacheVersion: string;
  /** HTTP status of the failed offline replay. */
  status: number;
  /** Serialized mutation variables (for export / manual repair). */
  variables: unknown;
  /** Error payload returned by the server, if any. */
  error?: unknown;
  /** Epoch ms when quarantined. */
  createdAt: number;
}

function productTypeOf(mutationKey: unknown): ProductEntityType | undefined {
  const head = Array.isArray(mutationKey) ? mutationKey[0] : undefined;
  return appConfig.productEntityTypes.find((type) => type === head);
}

/** What a caller passes: `mutationKey` names the product type, and the running bundle's cache version is stamped here. */
type QuarantineInput = Omit<FailedSyncRecord, 'id' | 'createdAt' | 'clientCacheVersion'> & { mutationKey?: unknown; clientCacheVersion?: string };

/** Quarantine a failed mutation. De-duplicates on `mutationId`. No-ops while signed out. */
export async function quarantineFailedSync({ mutationKey, ...record }: QuarantineInput): Promise<void> {
  const db = getLocalUserDb();
  if (!db) return;
  try {
    const existing = await db.failedSync.where('mutationId').equals(record.mutationId).first();
    if (existing) return;
    await db.failedSync.add({
      entityType: productTypeOf(mutationKey),
      clientCacheVersion: appConfig.clientCacheVersion,
      ...record,
      createdAt: Date.now(),
    });
  } catch (error) {
    console.error('[failed-sync] Failed to quarantine mutation:', error);
    reportCriticalError('offline.quarantine_failed', error, { mutation_id: record.mutationId });
  }
}

/**
 * List quarantined mutations, newest first. Empty while signed out. No UI reads the quarantine yet; this and the two
 * below are its read and repair functions.
 * @public
 */
export async function listFailedSync(): Promise<FailedSyncRecord[]> {
  const db = getLocalUserDb();
  if (!db) return [];
  return db.failedSync.orderBy('createdAt').reverse().toArray();
}

/**
 * Remove a quarantined record once manually replayed/repaired.
 * @public
 */
export async function clearFailedSync(id: number): Promise<void> {
  await getLocalUserDb()?.failedSync.delete(id);
}

/**
 * Export all quarantined records as a JSON string for support/repair.
 * @public
 */
export async function exportFailedSync(): Promise<string> {
  return JSON.stringify(await listFailedSync(), null, 2);
}
